import { useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { IconBrushMagic } from '@picsart/design-system/foundation/icons/IconBrushMagic';
import { BaseBottomSheet } from '@picsart/design-system/components/BaseBottomSheet';
import { PanelHeading } from '@picsart/design-system/components/PanelHeading';
import { useTranslator } from '@picsart/rc/services/localization';

import { useAppStore, type IAppStore } from '@/store';
import useAspectRatio from '@/hooks/useAspectRatio';
import { useDeviceViewport } from '@/hooks/useViewport';
import { InputType } from '@/types';
import useStyles from './styles';

import PreviewSection from '../PreviewSection';
import InputsSection from '../InputsSection';
import CreateFooter from '../CreateFooter';

const selector = (store: IAppStore) => ({
  selectedPreset: store.selectedPreset,
  inputs: store.inputs,
});

/**
 * Layout for the Create panel. Owns only the cross-section concerns that
 * can't live in either child: the content/upload refs that useAspectRatio
 * measures together, and the shared aspect-ratio output that PreviewSection
 * needs but is recomputed when inputs change.
 *
 * State that belongs to a single section is owned by that section.
 */
const CreateContent = () => {
  const classes = useStyles();
  const translate = useTranslator();
  const { selectedPreset, inputs } = useAppStore(selector);
  const { isMobile } = useDeviceViewport();

  const contentRef = useRef<HTMLDivElement>(null);
  const uploadSectionRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);

  const [isDescriptionSheetOpen, setIsDescriptionSheetOpen] = useState(false);
  const [isDescriptionOverflowing, setIsDescriptionOverflowing] =
    useState(false);

  const hasUploadedMedia = inputs.some(
    (i) => (i.type === InputType.IMAGE || i.type === InputType.VIDEO) && i.src
  );

  const { previewAspectRatio, calculateAspectRatio } = useAspectRatio({
    contentRef,
    uploadSectionRef,
    hasUploadedMedia,
    presetFlowId: selectedPreset?.flowId,
  });

  // Detect whether the 2-line clamp is actually clipping content so the
  // inline "Show more" stays hidden for descriptions that fit in two lines.
  useLayoutEffect(() => {
    const el = descriptionRef.current;
    if (!el || !isMobile) {
      setIsDescriptionOverflowing(false);
      return;
    }
    setIsDescriptionOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [isMobile, selectedPreset?.description]);

  if (!selectedPreset) return null;

  const showInlineToggle = isMobile && isDescriptionOverflowing;
  const closeDescriptionSheet = () => setIsDescriptionSheetOpen(false);

  return (
    <div className={classes.container} key={selectedPreset.flowId}>
      <div className={classes.header}>
        <IconBrushMagic fill="currentColor" className={classes.headerIcon} />
        {translate('Create')}
      </div>
      <div ref={contentRef} className={classes.content}>
        <PreviewSection
          previewAspectRatio={previewAspectRatio}
          hasUploadedMedia={hasUploadedMedia}
          onPreviewImageLoad={calculateAspectRatio}
        />
        {selectedPreset.description && (
          <div className={classes.descriptionWrapper}>
            <p
              ref={descriptionRef}
              className={classNames(classes.presetDescription, {
                [classes.presetDescriptionClamped]: isMobile,
                [classes.presetDescriptionWithToggle]:
                  isMobile && showInlineToggle,
                [classes.presetDescriptionExpanded]: !isMobile,
              })}
            >
              {selectedPreset.description}
            </p>
            {showInlineToggle && (
              <button
                type="button"
                className={classes.descriptionToggle}
                onClick={() => setIsDescriptionSheetOpen(true)}
              >
                {translate('Show more')}
              </button>
            )}
          </div>
        )}
        <InputsSection uploadSectionRef={uploadSectionRef} />
      </div>
      <CreateFooter />
      {isMobile && selectedPreset.description && (
        <BaseBottomSheet
          isOpen={isDescriptionSheetOpen}
          onClose={closeDescriptionSheet}
          snapPoints={[360]}
          defaultActiveIndex={0}
        >
          <div className={classes.descriptionSheetHeading}>
            <PanelHeading
              title={selectedPreset.title || translate('Description')}
              closeAction={closeDescriptionSheet}
            />
          </div>
          <div className={classes.descriptionSheetBody}>
            {selectedPreset.description}
          </div>
        </BaseBottomSheet>
      )}
    </div>
  );
};

export default CreateContent;
